import { ColumnOrderState, ColumnPinningState } from "@tanstack/react-table";

import { decodeIndex, encodeIndex, widthFor } from "./columnIndexCodec";

// Distinguishes "no left-pinned columns" from "param omitted (use default)".
// Index encoding only ever emits lowercase letters, so this never collides.
const EMPTY_LEFT_SENTINEL = "_";

// Encodes the left-pinned columns as fixed-width letter indices into
// `defaultOrder`, e.g. default `[a,b,c,d]`, left `[c,a]` → `"ca"` (right is never
// pinned). `null` when there's nothing to persist (matches default, unknown id,
// or over 676 columns); the sentinel marks an explicit empty list.
export function encodeColumnPinning(
    currentPinning: ColumnPinningState,
    defaultOrder: ColumnOrderState,
    defaultPinning: ColumnPinningState | undefined
): string | null {
    if (defaultOrder.length === 0) {
        return null;
    }
    const width = widthFor(defaultOrder.length);
    if (width === 0) {
        return null;
    }
    const currentLeft = currentPinning.left ?? [];
    const defaultLeft = defaultPinning?.left ?? [];
    if (arraysEqual(currentLeft, defaultLeft)) {
        return null;
    }
    if (currentLeft.length === 0) {
        return EMPTY_LEFT_SENTINEL;
    }
    const indexById = new Map<string, number>(
        defaultOrder.map((id, i) => [id, i])
    );
    const indices = currentLeft.map((id) => indexById.get(id));
    if (!indices.every((idx): idx is number => idx !== undefined)) {
        return null;
    }
    return indices.map((idx) => encodeIndex(idx, width)).join("");
}

// Decodes the letter indices back into left-pinned columns via `defaultOrder`;
// `right` is carried over from the default. `null` on anything malformed (bad
// chars, wrong length, out-of-range or duplicate indices) so the caller falls
// back to the default.
export function decodeColumnPinning(
    encoded: string,
    defaultOrder: ColumnOrderState,
    defaultPinning: ColumnPinningState | undefined
): ColumnPinningState | null {
    if (encoded.length === 0 || defaultOrder.length === 0) {
        return null;
    }
    const right = defaultPinning?.right ?? [];
    if (encoded === EMPTY_LEFT_SENTINEL) {
        return { left: [], right };
    }
    const width = widthFor(defaultOrder.length);
    if (width === 0) {
        return null;
    }
    if (encoded.length % width !== 0) {
        return null;
    }
    const indices = Array.from({ length: encoded.length / width }, (_, k) =>
        decodeIndex(encoded.slice(k * width, k * width + width))
    );
    if (
        !indices.every(
            (idx): idx is number =>
                idx !== null && idx >= 0 && idx < defaultOrder.length
        )
    ) {
        return null;
    }
    if (new Set(indices).size !== indices.length) {
        return null;
    }
    return { left: indices.map((idx) => defaultOrder[idx]), right };
}

function arraysEqual(
    a: ReadonlyArray<string>,
    b: ReadonlyArray<string>
): boolean {
    return a.length === b.length && a.every((value, i) => value === b[i]);
}
